import { Chip, CircularProgress, Tooltip } from '@mui/material';
import { CircleAlert } from 'lucide-react';
import { useIntl } from 'react-intl';

type Props = {
    /** Series still queued or downloading for the patient. */
    inFlight: number;
    /** Series whose retrieval failed and can be asked for again. */
    failed: number;
};

/**
 * A patient row's import at a glance: a quiet "Importing" chip while series are still arriving and
 * a red one while any has failed, so a patient needing attention is found without opening each
 * row. Nothing once every requested series has settled without a failure. The per-series detail,
 * and the retry and cancel, are in the patient panel.
 */
export default function ImportActivityChip({ inFlight, failed }: Props) {
    const intl = useIntl();
    if (inFlight <= 0 && failed <= 0) {
        return null;
    }
    const failing = failed > 0;
    const label = failing
        ? intl.formatMessage(
              { id: 'patientList.retrievals.activity.failed' },
              { count: failed }
          )
        : intl.formatMessage(
              { id: 'patientList.retrievals.activity.importing' },
              { count: inFlight }
          );
    return (
        <Tooltip
            title={intl.formatMessage(
                { id: 'patientList.retrievals.activity.tooltip' },
                { inFlight, failed }
            )}
        >
            <Chip
                data-testid="patient-import-chip"
                size="small"
                variant="outlined"
                color={failing ? 'error' : 'info'}
                icon={
                    failing ? (
                        <CircleAlert size={11} />
                    ) : (
                        <CircularProgress size={9} color="inherit" />
                    )
                }
                label={label}
                sx={{
                    height: 18,
                    fontSize: 10,
                    alignSelf: 'center',
                    '& .MuiChip-icon': { ml: 0.5 },
                }}
            />
        </Tooltip>
    );
}
